import { Link } from '@tanstack/react-router'
import { useState, useSyncExternalStore } from 'react'
import { consommerInvitation, lireInvitation, surInvitation } from '../pwa/enregistrement'
import {
  detecterPlateforme,
  estInstallee,
  memoriserRefusInstallation,
  type Plateforme,
} from '../pwa/plateforme'
import { EntetePage } from '../composants/Ui'

// Installer l'appli sur l'écran d'accueil (porté de l'accueil d'installation de flowAI) :
// iPhone (Safari seulement, pas d'invite : « Partager », puis « Sur l'écran d'accueil »),
// Android (invite de Chrome, sinon menu), navigateur intégré à une autre appli (Gmail,
// WhatsApp…) qui ne peut pas installer, ordinateur.

function useInvitation() {
  return useSyncExternalStore(surInvitation, lireInvitation, () => null)
}

function CopierAdresse() {
  const [copie, setCopie] = useState(false)
  const adresse = typeof window === 'undefined' ? '' : window.location.origin
  return (
    <p>
      <code className="adresse">{adresse}</code>{' '}
      <button
        type="button"
        className="bouton-discret"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(adresse)
            setCopie(true)
          } catch {
            setCopie(false)
          }
        }}
      >
        {copie ? 'Adresse copiée' : 'Copier l’adresse'}
      </button>
    </p>
  )
}

/** Après l'installation : se connecter une fois dans l'appli (ses cookies sont à part). */
function ApresInstallation() {
  return (
    <p className="discret">
      Ouvrez ensuite l’appli depuis son icône et connectez-vous une fois avec Google : sur iPhone,
      l’appli installée ne partage pas la connexion de Safari. La connexion dure ensuite trente
      jours, prolongée à chaque utilisation.
    </p>
  )
}

function Etapes({ plateforme }: { plateforme: Plateforme }) {
  const invitation = useInvitation()
  const [enCours, setEnCours] = useState(false)
  const installer = async () => {
    if (!invitation || enCours) return
    setEnCours(true)
    try {
      await invitation.prompt()
      await invitation.userChoice
    } finally {
      consommerInvitation()
      setEnCours(false)
    }
  }
  switch (plateforme) {
    case 'installee':
      return <p>L’appli est installée sur cet appareil.</p>
    case 'ios-safari':
      return (
        <>
          <ol className="etapes">
            <li>
              Touchez <strong>Partager</strong> (le carré avec une flèche vers le haut), en bas de
              l’écran.
            </li>
            <li>
              Faites défiler et touchez <strong>Sur l’écran d’accueil</strong>.
            </li>
            <li>
              Touchez <strong>Ajouter</strong>.
            </li>
          </ol>
          <ApresInstallation />
        </>
      )
    case 'ios-autre':
      return (
        <>
          <p>
            Sur iPhone, seul <strong>Safari</strong> peut installer l’appli. Ouvrez cette adresse
            dans Safari, puis suivez les étapes proposées :
          </p>
          <CopierAdresse />
        </>
      )
    case 'integre':
      return (
        <>
          <p>
            Vous êtes dans le navigateur d’une autre appli (Gmail, WhatsApp, Facebook…), qui ne peut
            pas installer. Ouvrez l’adresse dans <strong>Safari</strong> (iPhone) ou{' '}
            <strong>Chrome</strong> (Android) : menu <strong>⋯</strong>, puis « Ouvrir dans le
            navigateur », ou copiez l’adresse.
          </p>
          <CopierAdresse />
        </>
      )
    case 'android':
      return (
        <>
          {invitation && (
            <p>
              <button
                type="button"
                className="bouton bouton-grand"
                onClick={installer}
                disabled={enCours}
              >
                {enCours ? 'Installation…' : 'Installer l’appli'}
              </button>
            </p>
          )}
          <p>{invitation ? 'Ou, dans Chrome :' : 'Dans Chrome :'}</p>
          <ol className="etapes">
            <li>
              Touchez le menu <strong>⋮</strong> en haut à droite.
            </li>
            <li>
              Touchez <strong>Installer l’application</strong> (ou « Ajouter à l’écran d’accueil »).
            </li>
          </ol>
          <ApresInstallation />
        </>
      )
    default:
      return (
        <>
          <p>
            Sur ordinateur, l’appli s’utilise dans le navigateur. Chrome et Edge peuvent aussi
            l’installer (icône à droite de la barre d’adresse).
          </p>
          {invitation && (
            <button type="button" className="bouton" onClick={installer} disabled={enCours}>
              Installer l’appli
            </button>
          )}
        </>
      )
  }
}

export function Installer() {
  const plateforme: Plateforme = estInstallee() ? 'installee' : detecterPlateforme(window.navigator)
  return (
    <main>
      <EntetePage surtitre="Téléphone" titre="Installer l’appli" />
      <p className="discret">
        Installée sur l’écran d’accueil, l’appli s’ouvre en plein écran, comme une appli du
        téléphone, pour photographier un courrier et suivre son rangement.
      </p>
      <section className="carte section">
        <Etapes plateforme={plateforme} />
      </section>
      <p>
        <Link to="/">Retour à l’accueil</Link>
      </p>
    </main>
  )
}

/** Proposition d'installation sur l'accueil, sur téléphone seulement ; refus gardé une semaine. */
export function BanniereInstallation({ refusee }: { refusee: boolean }) {
  const [masquee, setMasquee] = useState(refusee)
  if (masquee || estInstallee()) return null
  const plateforme = detecterPlateforme(window.navigator)
  if (plateforme === 'ordinateur') return null
  return (
    <section className="carte banniere-installation">
      <p>
        <strong>Installer l’appli sur ce téléphone</strong> pour photographier un document en un
        geste.
      </p>
      <div className="boutons">
        <Link to="/installer" className="bouton">
          Comment faire
        </Link>
        <button
          type="button"
          className="bouton-discret"
          onClick={() => {
            memoriserRefusInstallation()
            setMasquee(true)
          }}
        >
          Plus tard
        </button>
      </div>
    </section>
  )
}
